import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Loader2, Trash2, Bot, User, BarChart3, RefreshCw, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import axios from "axios";
import { toast } from "sonner";

import { API_URL as API } from "@/lib/api";
const CHART_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1"];

const SUGGESTIONS = [
  "¿Cuántos productos tienen stock menor a 5?",
  "Top 10 familias con mayor stock en almacén",
  "¿Qué productos enviamos a Manco Kapac que no reportaron ingreso?",
  "Compara inventario de Manco Kapac vs Plaza Bonita",
  "¿Cuál es el valor total del inventario en Bs?",
  "Genera un reporte PDF del inventario de Plaza Bonita",
];

const InlineChart = ({ config }) => {
  if (!config) return null;

  // Normalize chart config from various Gemini formats
  let type = config.type;
  let chartData = config.data;
  let title = config.title || "";
  const dKey = config.dataKey || "value";
  const nKey = config.nameKey || "name";

  // Handle nested format: {action:"chart", data: {type:"bar", data: {labels:[], datasets:[]}}}
  if (chartData && typeof chartData === "object" && !Array.isArray(chartData)) {
    if (chartData.type && !type) type = chartData.type;
    if (chartData.title && !title) title = chartData.title;
    if (chartData.data) chartData = chartData.data;
  }

  type = type || "bar";

  // Handle Chart.js format (labels + datasets)
  if (chartData && !Array.isArray(chartData) && chartData.labels && chartData.datasets) {
    const labels = chartData.labels;
    const values = chartData.datasets[0]?.data || [];
    const label = chartData.datasets[0]?.label || "";
    if (!title && label) title = label;
    chartData = labels.map((l, i) => ({ name: String(l), value: values[i] || 0 }));
  }

  if (!Array.isArray(chartData) || chartData.length === 0) return null;

  return (
    <div className="my-3 p-3 bg-card rounded-lg border" data-testid="chat-inline-chart">
      {title && <p className="text-xs font-semibold text-foreground mb-2">{title}</p>}
      <ResponsiveContainer width="100%" height={220}>
        {type === "pie" ? (
          <PieChart>
            <Pie data={chartData} dataKey={dKey} nameKey={nKey} cx="50%" cy="50%" outerRadius={75} innerRadius={35}
              label={({ name, percent }) => `${(name || "").slice(0, 15)} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 9 }}>
              {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Tooltip />
          </PieChart>
        ) : type === "line" ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={nKey} tick={{ fontSize: 9 }} />
            <YAxis tick={{ fontSize: 9 }} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey={dKey} stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        ) : (
          <BarChart data={chartData}>
            <XAxis dataKey={nKey} tick={{ fontSize: 8 }} angle={-35} textAnchor="end" height={70} />
            <YAxis tick={{ fontSize: 9 }} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Bar dataKey={dKey} fill="#0ea5e9" radius={[4, 4, 0, 0]}>
              {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const formatMarkdown = (text) => {
  if (!text) return "";
  // Remove any remaining JSON code blocks
  let cleaned = text.replace(/```json\s*\{[\s\S]*?\}\s*```/g, '').trim();

  // Process markdown tables first
  const lines = cleaned.split('\n');
  let html = '';
  let inTable = false;
  let tableRows = [];

  const flushTable = () => {
    if (tableRows.length === 0) return;
    html += '<div class="overflow-x-auto my-2"><table class="w-full border-collapse text-xs">';
    tableRows.forEach((row, ri) => {
      const tag = ri === 0 ? 'th' : 'td';
      const cls = ri === 0
        ? 'px-3 py-1.5 bg-muted font-semibold text-foreground border border-border text-left'
        : 'px-3 py-1.5 border border-border text-muted-foreground';
      html += '<tr>' + row.map(c => `<${tag} class="${cls}">${c.replace(/\*\*/g, '')}</${tag}>`).join('') + '</tr>';
    });
    html += '</table></div>';
    tableRows = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.slice(1, -1).split('|').map(c => c.trim());
      if (cells.every(c => /^[-:]+$/.test(c))) { continue; } // separator
      tableRows.push(cells);
      inTable = true;
    } else {
      if (inTable) { flushTable(); inTable = false; }
      let l = trimmed;
      l = l.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      l = l.replace(/\*(.+?)\*/g, '<em>$1</em>');
      l = l.replace(/`([^`]+)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-[11px] font-mono">$1</code>');
      if (l.startsWith('### ')) { html += `<p class="font-bold text-sm text-foreground mt-3 mb-1">${l.slice(4)}</p>`; }
      else if (l.startsWith('## ')) { html += `<p class="font-bold text-base text-foreground mt-3 mb-1">${l.slice(3)}</p>`; }
      else if (l.startsWith('- ')) { html += `<p class="ml-3 text-sm text-foreground">• ${l.slice(2)}</p>`; }
      else if (/^\d+\.\s/.test(l)) { html += `<p class="ml-3 text-sm text-foreground">${l}</p>`; }
      else if (l === '') { html += '<br/>'; }
      else { html += `<p class="text-sm text-foreground">${l}</p>`; }
    }
  }
  if (inTable) flushTable();
  return html;
};

const AsistenteIA = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => `session_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/chat/message`, {
        message: msg,
        session_id: sessionId,
      }, { timeout: 120000 });

      const data = res.data;
      const charts = (data.charts || []).filter(c => c?.data?.length > 0);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.response,
        charts,
        actions: data.actions || [],
      }]);
      if (data.session_id) setSessionId(data.session_id);
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.message || "Error de conexión";
      setMessages(prev => [...prev, { role: "assistant", content: `Error: ${errMsg}`, isError: true }]);
      toast.error(errMsg);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearChat = async () => {
    try { await axios.delete(`${API}/api/chat/history/${sessionId}`); } catch (e) { /* ignore */ }
    setMessages([]);
    setSessionId(`session_${Date.now()}`);
    toast.success("Chat limpiado");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="asistente-ia-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-foreground" data-testid="asistente-title">Asistente IA</h1>
            <p className="text-[10px] text-muted-foreground">Consulta inventarios, genera reportes y más</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={clearChat} className="text-xs gap-1.5 h-7 text-muted-foreground"
          data-testid="chat-clear-btn">
          <Trash2 className="w-3 h-3" /> Limpiar
        </Button>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto rounded-xl border bg-muted/50 p-4 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-100 to-indigo-100 flex items-center justify-center">
              <Bot className="w-8 h-8 text-[#FF9900]" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-foreground mb-1">Pregunta lo que necesites</p>
              <p className="text-xs text-muted-foreground max-w-md">
                Puedo consultar inventarios, generar reportes, analizar diferencias entre almacén y locales, y más.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)}
                  className="text-left text-xs text-muted-foreground bg-card border border-border rounded-lg px-3 py-2.5 hover:bg-[#FF9900]/10 hover:border-[#FF9900] transition-colors"
                  data-testid={`suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`chat-msg-${i}`}>
            {msg.role === "assistant" && (
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shrink-0 mt-0.5">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
            )}
            <div className={`max-w-[80%] ${msg.role === "user"
              ? "bg-[#FF9900] text-white rounded-2xl rounded-tr-md px-4 py-2.5"
              : msg.isError
                ? "bg-rose-50 border border-rose-200 rounded-2xl rounded-tl-md px-4 py-2.5"
                : "bg-card border border-border rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm"
            }`}>
              {msg.role === "user" ? (
                <p className="text-sm">{msg.content}</p>
              ) : (
                <>
                  <div className="text-sm text-foreground leading-relaxed prose-sm"
                    dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }} />
                  {msg.charts?.map((chart, ci) => <InlineChart key={ci} config={chart} />)}
                  {msg.actions?.map((a, ai) => (
                    <div key={ai} className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                      {a.action === "sync" && `Sincronización de ${a.target}: ${a.error ? "Error" : "exitosa"}`}
                      {a.action === "generate_report" && a.result?.filename && (
                        <span>Reporte generado: <strong>{a.result.filename}</strong>
                          {a.result.download_url && (
                            <a href={`${API}${a.result.download_url}`} target="_blank" rel="noreferrer"
                              className="ml-2 text-[#FF9900] underline">Descargar</a>
                          )}
                        </span>
                      )}
                      {a.action === "cruce" && a.kpis && `Cruce: ${a.kpis.total_registros} registros, ${a.kpis.pct_coincidencia}% coincidencia`}
                    </div>
                  ))}
                </>
              )}
            </div>
            {msg.role === "user" && (
              <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-2.5" data-testid="chat-loading">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shrink-0">
              <Bot className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="bg-card border border-border rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#FF9900]" />
                <span className="text-xs text-muted-foreground">Pensando...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="pt-3 shrink-0">
        <div className="flex items-center gap-2">
          <Input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown} placeholder="Escribe tu pregunta..."
            disabled={loading} className="flex-1 h-10 text-sm"
            data-testid="chat-input" />
          <Button onClick={() => sendMessage()} disabled={loading || !input.trim()}
            className="h-10 px-4 bg-[#FF9900] hover:bg-[#E68A00] text-white gap-1.5"
            data-testid="chat-send-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1 text-center">
          Powered by Gemini. Las respuestas se basan en los datos sincronizados del sistema.
        </p>
      </div>
    </div>
  );
};

export default AsistenteIA;
